import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Heading from '../../../../generic-components/Heading';
import { STYLE, TEXT } from '../../constants';
import { convertValueToUSD, convertValueToUSDv2 } from '../../../../helpers/currency.helper';
import { ENTER_KEY } from '../../../../constants/general';

const {
  PAGES: {
    POST_LINKED: { MONEY_TRANSFER: pageStyles },
  },
} = STYLE;

const {
  PAGES: {
    POST_LINKED: { MONEY_TRANSFER: pageContent },
  },
} = TEXT;

interface IProps {
  onTransferValue: (data: string) => void;
  onFocus: () => void;
  accountId: number;
  balance: number;
  error: string;
  isDisabledInput: boolean;
  onSubmit?: () => void;
}

export const TransferBody = ({
  onTransferValue,
  accountId,
  error,
  onFocus,
  balance,
  isDisabledInput,
  onSubmit,
}: IProps) => {
  const { t } = useTranslation();
  const elInput = useRef(null);
  const [transferValue, setTransferValue] = useState<string>('0');

  const shouldShowBalance = useMemo(() => {
    if (balance && balance > 0) {
      return true;
    }
    return false;
  }, [balance]);

  const getCleanValue = (value: string) => value.replace('$', '').replaceAll(',', '');

  const convertBalanceToDisplay = (amount: number) => convertValueToUSD(amount, 2);

  const handleFocus = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setTimeout(() => {
      event.target.selectionStart = value.length;
      event.target.selectionEnd = value.length;
    }, 50);
    onFocus();
  };

  const handleTransferValue = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    const dotSeparatorsArr = value.split('').filter((i) => i === '.');
    let cleanValue = getCleanValue(value);

    if (!/^[\d.-]*$/.test(cleanValue)) return;

    if (cleanValue.length > 1 && cleanValue.charAt(0) === '0') {
      cleanValue = cleanValue.substring(1);
    }

    if (dotSeparatorsArr.length > 1) {
      return;
    }

    setTransferValue(cleanValue);
    onTransferValue(cleanValue);
  };

  const onBlur = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    let cleanValue = getCleanValue(value);

    if (cleanValue === '-' || cleanValue === '.' || cleanValue === '0.' || !cleanValue) {
      cleanValue = '0';
    }
    setTransferValue(cleanValue);
  };

  const formattedValue = useMemo(() => {
    if (!transferValue) return '';
    if (transferValue.slice(-1) === '.') {
      return transferValue.length === 1 ? '0.' : `${convertValueToUSDv2(Number(transferValue))}.`;
    }
    return convertValueToUSDv2(Number(transferValue));
  }, [transferValue]);

  useEffect(() => {
    if (elInput.current) {
      (elInput.current as HTMLInputElement).focus();
    }
  }, []);

  useEffect(() => {
    setTransferValue('');
  }, [accountId]);

  return (
    <div className={pageStyles.BODY.WRAPPER}>
      <Heading title={t(pageContent.BODY.HEADING)} className={pageStyles.BODY.MAIN_HEADING} />
      {shouldShowBalance && (
        <p className='font-metric text-base text-bw07 mb-[24px] text-center'>
          <span className='text-xl text-action03 font-metricMedium'>{`${convertBalanceToDisplay(balance)} `}</span>
          {t(pageContent.BODY.BALANCE_LINE_1)}
          <br />
          {t(pageContent.BODY.BALANCE_LINE_2)}
        </p>
      )}
      <input
        onFocus={handleFocus}
        onKeyDown={(event) => {
          if (event.key === ENTER_KEY && !error && onSubmit) {
            onSubmit();
          }
        }}
        disabled={isDisabledInput}
        ref={elInput}
        onChange={handleTransferValue}
        onBlur={onBlur}
        className={error ? pageStyles.BODY.INPUT_DISABLED : pageStyles.BODY.INPUT}
        placeholder='$'
        type='text'
        value={`$${formattedValue || '0'}`}
      />
      {error && (
        <p id='moneyTransferError' className={pageStyles.BODY.ERROR}>
          {error}
        </p>
      )}
    </div>
  );
};
